import Styles from '../6.manage/items/memo/styles.module.css';
import Own from './closePeriodModal.module.css';
import React, { useEffect, useState } from 'react';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

type Props = {
  companyId: string;
  periodIndex: number;
  periodName: string;
  counts: { hired: number; rejected: number; withdrawn: number; undecided: number };
  // 開いている間に、別の画面(ほかのタブ・ほかの人)でこの回が締められた
  alreadyClosed: boolean;
  onClose: () => void;
  onClosed: () => void;
};

// 「この募集回を締める」の確認。締めると、その回の応募の記録(アーカイブ)が固定され、
// 次の募集回の受付が始まる(Cloud Functions の closeRecruitPeriod が行う)。
// 結論が出ていない学生がいても締められる。その学生は「結論なし」として残り、後から結論を出せる。
// (結論が出るまで次の回に応募できないようにするのは、学生アプリの再応募の対応と一緒に入れる。
//  それまでは確認画面にもその説明は出さない)
// 見た目はメモの編集モーダル(6.manage/items/memo)と同じ。
const ClosePeriodModal: React.FC<Props> = ({ companyId, periodIndex, periodName, counts, alreadyClosed, onClose, onClosed }) => {

  const [ closing, setClosing ] = useState(false);
  const [ error, setError ] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !closing) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  async function close() {
    if (closing) return;
    setClosing(true);
    setError('');
    try {
      const closeRecruitPeriod = httpsCallable(getFunctions(getApp(), 'asia-northeast1'), 'closeRecruitPeriod');
      await closeRecruitPeriod({ companyId, periodIndex });
      onClosed();
    } catch (e: any) {
      console.error('募集回を締められませんでした', e);
      // サーバーが理由を返すエラー(権限がない・締められる回ではない等)は、その文言を出す。
      // それ以外(通信の失敗・関数が無い等。code は 'functions/internal' のように付く)は共通の文言にする。
      const code = String(e?.code ?? '');
      const known = ['permission-denied', 'failed-precondition', 'invalid-argument', 'not-found', 'unauthenticated'];
      setError(known.some((k) => code.endsWith(k)) && e?.message
        ? e.message
        : '締められませんでした。時間をおいて、もう一度お試しください。');
      setClosing(false);
    }
  }

  // 別の画面で締められた(この画面から締めている途中でなければ)。締め直しはできないので、閉じるだけにする。
  const closedElsewhere = alreadyClosed && !closing;

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true' aria-labelledby='close-period-title'>
      <div className={Styles.scrim} onClick={() => !closing && onClose()} />
      <div className={Styles.card}>
        <h3 id='close-period-title' className={Styles.title}>{periodName}を締めますか？</h3>
        <p className={Styles.subtitle}>締めると、この募集回の選考結果と評価が記録として固定され、次の募集回の受付が始まります。</p>

        <div className={Own.counts}>
          <div><span>採用</span><strong>{counts.hired}人</strong></div>
          <div><span>不採用</span><strong>{counts.rejected}人</strong></div>
          <div><span>辞退</span><strong>{counts.withdrawn}人</strong></div>
          <div className={counts.undecided > 0 ? Own.undecided : undefined}><span>結論なし</span><strong>{counts.undecided}人</strong></div>
        </div>

        {counts.undecided > 0 && (
          <p className={Own.note}>
            結論が出ていない学生が{counts.undecided}人います。締めた後も、この募集回から結論（採用・不採用・辞退）を出すことができます。
          </p>
        )}

        {closedElsewhere
          ? <p className={Styles.error} role='alert'>{periodName}は、別の画面ですでに締められました。</p>
          : error && <p className={Styles.error} role='alert'>{error}</p>}

        <div className={Styles.actions}>
          {closedElsewhere ? (
            <button type='button' className={Styles.confirm} onClick={onClose}>
              閉じる
            </button>
          ) : (
            <>
              <button type='button' className={Styles.cancel} disabled={closing} onClick={onClose}>
                キャンセル
              </button>
              <button type='button' className={Styles.confirm} disabled={closing} onClick={close}>
                {closing ? '締めています...' : '締める'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClosePeriodModal;
